import {
  HttpLink, InMemoryCache, ApolloClient, NormalizedCacheObject, from,
} from '@apollo/client';
import { persistCache } from 'apollo-cache-persist';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { SentryLink } from 'apollo-link-sentry';
import * as Sentry from '@sentry/nextjs';
import { some } from 'lodash';
import AuthTokenStore from './authTokenStore';
import { getCadToolName, getFromSketchupStorage, getTokenFromSketchupStorage } from './sketchupStorage';

// LocalStorage quota constants
const BYTES_PER_MB = 1048576;
const CACHE_SIZE_WARNING_THRESHOLD = BYTES_PER_MB * 1.5; // 1.5MB
const CACHE_SIZE_MAX = BYTES_PER_MB * 2; // 2MB
const CACHE_PERSIST_DEBOUNCE_MS = 1000; // 1 second

/**
 * Checks if an error is a localStorage quota exceeded error
 * Handles different error formats across browsers (Chrome, Safari, Firefox)
 * @param error - Error object to check
 * @returns true if the error is a quota exceeded error
 */
function isQuotaExceededError(error: any): boolean {
  return error.name === 'QuotaExceededError'
    || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || error.code === 22
    || error.code === 1014;
}

function InitApolloClient(): ApolloClient<NormalizedCacheObject> {
  const cadToolName = typeof window !== 'undefined' ? getCadToolName() : '';
  const uri = `${process.env.NEXT_PUBLIC_APP_URL}/graphql`;
  const ssrMode = typeof window === 'undefined';
  const httpLink = new HttpLink({
    uri,
  });

  let token;
  const authObj = (authToken) => ({
    headers: {
      authorization: `Bearer ${authToken}`,
    },
  });
  const withToken = setContext(() => {
    // if you have a cached value, return it immediately
    if (token) {
      return authObj(token);
    }

    if (!ssrMode && cadToolName !== 'Sketchup') {
      return authObj(AuthTokenStore.get());
    }

    return getFromSketchupStorage('token').then((userToken) => {
      if (userToken?.includes('ey')) {
        token = userToken;
        return authObj(userToken);
      }
      return getTokenFromSketchupStorage().then((userToken2) => {
        if (userToken2?.includes('ey')) {
          token = userToken2;
          return authObj(userToken2);
        }
        return authObj(AuthTokenStore.get());
      });
    });
  });

  const sentryLink = new SentryLink({
    uri,
    // attachBreadcrumbs: {
    //   includeQuery: true,
    //   includeVariables: true,
    //   includeError: true,
    // },
  });

  const resetToken = onError(({ networkError, graphQLErrors }) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (networkError && networkError.name === 'ServerError' && networkError?.statusCode === 401) {
      // remove cached token on 401 from the server
      token = null;
    }

    if (networkError && Array.isArray(networkError) && some(networkError, (error) => error.name.includes('Authentication token'))) {
      token = null;
    }
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.warn(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
        Sentry.captureMessage(message);
      });
    }
  });

  const cache = new InMemoryCache({
    addTypename: false,
  });

  if (!ssrMode) { // if on client machine
    // Create a safe storage wrapper that handles quota errors
    const safeStorage = {
      getItem: (key: string): string | null => {
        try {
          return window.localStorage.getItem(key);
        } catch (error) {
          console.warn('Failed to read from localStorage:', error);
          return null;
        }
      },
      setItem: (key: string, value: string): void => {
        try {
          // Check if the value would exceed quota before writing
          const currentSize = new Blob([value]).size;
          if (currentSize > CACHE_SIZE_WARNING_THRESHOLD) {
            console.warn('Cache size approaching limit, clearing old cache');
            window.localStorage.removeItem('apollo-cache-persist');
          }
          window.localStorage.setItem(key, value);
        } catch (error) {
          // If quota exceeded, clear old cache and try again
          if (isQuotaExceededError(error)) {
            console.warn('Quota exceeded, clearing apollo-cache-persist');
            try {
              window.localStorage.removeItem('apollo-cache-persist');
              // Try once more with the cleared cache
              try {
                window.localStorage.setItem(key, value);
              } catch (retryError) {
                console.error('Failed to persist cache after clearing:', retryError);
                Sentry.captureException(retryError, {
                  tags: { component: 'ApolloCachePersist', action: 'retryAfterClear' },
                });
              }
            } catch (clearError) {
              console.error('Failed to clear cache:', clearError);
              Sentry.captureException(clearError, {
                tags: { component: 'ApolloCachePersist', action: 'clearCache' },
              });
            }
          } else {
            console.error('Unexpected localStorage error:', error);
            Sentry.captureException(error, {
              tags: { component: 'ApolloCachePersist' },
            });
          }
        }
      },
      removeItem: (key: string): void => {
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          console.warn('Failed to remove from localStorage:', error);
        }
      },
    };

    persistCache({
      cache: cache as any,
      storage: safeStorage as any,
      // Note: maxSize may not be a supported parameter in apollo-cache-persist
      // If it causes issues, remove it - the safeStorage wrapper handles quota errors
      maxSize: CACHE_SIZE_MAX,
      debounce: CACHE_PERSIST_DEBOUNCE_MS,
      trigger: 'write', // Only persist on cache writes, not reads
    }).catch((error) => {
      // If cache persistence fails (e.g., quota exceeded), clear old cache
      console.warn('Cache persistence failed, clearing old cache:', error);
      Sentry.captureException(error, {
        tags: { component: 'ApolloCachePersist' },
        extra: { errorMessage: error.message },
      });
      try {
        window.localStorage.removeItem('apollo-cache-persist');
        console.log('Cleared apollo-cache-persist from localStorage');
      } catch (clearError) {
        console.error('Failed to clear cache:', clearError);
        Sentry.captureException(clearError, {
          tags: { component: 'ApolloCachePersist', action: 'clearCache' },
        });
      }
    });
  }

  return new ApolloClient({
    link: from([withToken, resetToken, sentryLink, httpLink]),
    cache,
    defaultOptions: {
      query: { errorPolicy: 'all', fetchPolicy: 'cache-first' },
    },
  });
}

export default InitApolloClient;
